import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import HttpClient from '../../../services/HttpClient'
import { fetchPlayableHans } from '../../../features/travel/api'
import Button from '../../../components/Button/Button'
import AppContext from '../../../contexts/AppContext'
import Field from '../../../components/Form/Field/Field'
import Select from '../../../components/Form/Select/Select'
import FormErrors from '../../../components/FormErrors/FormErrors'
import BBCodeEditor from '../../../components/BBCodeEditor/BBCodeEditor'
import { computeDerivedPVCK } from '../shared/characterDerivedStats'
import StatAllocationPanel from '../shared/StatAllocationPanel'
import { TOTAL_POINTS, emptyStats, STAT_KEYS } from '../shared/statAllocation'
import { fetchPublicSettings, DEFAULT_WIZARD_PATH } from '../../../services/settings.api'
import './createCharacter.css'

/**
 * Wizard de creación: paso 0 = Estudiante vs Shinobi.
 * Estudiante: ficha reducida (sin atributos / rango / elemento).
 * Shinobi: flujo completo; nace Genin nivel 10 (revisión staff).
 * Textos del paso 0: admin (Configuración del juego) con fallback local.
 */

const GENDERS = [
  { value: 'Shinobi', label: 'Shinobi' },
  { value: 'Kunoichi', label: 'Kunoichi' },
]

const HANDS = [
  { value: 'Diestro', label: 'Diestro' },
  { value: 'Zurdo', label: 'Zurdo' },
  { value: 'Ambidiestro', label: 'Ambidiestro' },
]

const BLOOD_TYPES = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'].map((b) => ({ value: b, label: b }))

const ELEMENTS = ['Fuego', 'Agua', 'Viento', 'Rayo', 'Tierra'].map((e) => ({ value: e, label: e }))

const RANKS = ['Genin'].map((r) => ({ value: r, label: r }))

const NAME_REGEX = /^[a-zA-ZÀ-ÿ\u00f1\u00d1\s'-]+$/
const MIN_AGE = 12
const MAX_AGE = 60

const PATH_COPY_DEFAULT = {
  estudiante: {
    title: DEFAULT_WIZARD_PATH.wizard_path_estudiante_title,
    body: DEFAULT_WIZARD_PATH.wizard_path_estudiante_body,
  },
  shinobi: {
    title: DEFAULT_WIZARD_PATH.wizard_path_shinobi_title,
    body: DEFAULT_WIZARD_PATH.wizard_path_shinobi_body,
  },
}

function stepsForPath(path) {
  if (!path) return ['path']
  if (path === 'estudiante') {
    return ['path', 'identity', 'origin', 'descriptions', 'history', 'summary']
  }
  return ['path', 'identity', 'origin', 'attributes', 'descriptions', 'history', 'summary']
}

export default function CreateCharacterWizard() {
  const { user, hasPermission } = useContext(AppContext)
  const navigate = useNavigate()

  const canCreateStaffChars = Boolean(
    hasPermission?.('character.academy.exam') ||
      hasPermission?.('admin.dashboard') ||
      hasPermission?.('admin.owner')
  )

  const [characters, setCharacters] = useState([])
  const maxSlots = user?.slotsShinobi ?? 0
  const numCharacters = characters.length
  const numEmptySlots = Math.max(0, maxSlots - numCharacters)

  const [clans, setClans] = useState([])
  const [playableHans, setPlayableHans] = useState([])

  const clanOptions = useMemo(() => clans.map((c) => ({ value: c.name, label: c.name })), [clans])
  const hanOptions = useMemo(
    () => playableHans.map((h) => ({ value: h.slug, label: h.name || h.slug })),
    [playableHans]
  )

  const [path, setPath] = useState(null) // 'estudiante' | 'shinobi'
  const [step, setStep] = useState(0)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [errors, setErrors] = useState([])
  const [pathCopy, setPathCopy] = useState(PATH_COPY_DEFAULT)
  /** Staff-only: pool compartido (evento / sensei). */
  const [staffFlags, setStaffFlags] = useState({ eventCharacter: false, sensei: false })

  const steps = useMemo(() => stepsForPath(path), [path])
  const stepId = steps[step] || 'path'
  const isStudent = path === 'estudiante'
  const createLevel = isStudent ? 1 : 10

  const [identity, setIdentity] = useState({
    name: '',
    gender: 'Shinobi',
    age: '',
    dominantHand: '',
    bloodType: '',
  })

  const [origin, setOrigin] = useState({
    clan: '',
    birthHanSlug: '',
    rank: 'Genin',
    element: '',
  })

  const selectedHanName = useMemo(() => {
    const han = playableHans.find((h) => h.slug === origin.birthHanSlug)
    return han?.name || origin.birthHanSlug || ''
  }, [playableHans, origin.birthHanSlug])

  const [stats, setStats] = useState(() => emptyStats())
  const [statsValid, setStatsValid] = useState(false)
  const [allocKey, setAllocKey] = useState(0)

  const [descriptions, setDescriptions] = useState({
    physical: '',
    psychological: '',
  })

  const [history, setHistory] = useState('')

  const { PV, CK } = useMemo(() => computeDerivedPVCK(stats, createLevel, 0, 0), [stats, createLevel])

  const onAllocChange = React.useCallback((payload) => {
    if (payload?.stats) setStats(payload.stats)
    setStatsValid(Boolean(payload?.valid))
  }, [])

  useEffect(() => {
    const initialize = async () => {
      try {
        setLoading(true)
        const [{ data: chars }, { data: clansData }, hans, settings] = await Promise.all([
          HttpClient().get('/api/v1/characters/mine'),
          HttpClient().get('/api/v1/public/clans'),
          fetchPlayableHans(),
          fetchPublicSettings().catch(() => null),
        ])
        setCharacters(chars || [])
        setClans(clansData || [])
        setPlayableHans(Array.isArray(hans) ? hans : [])
        if (settings) {
          setPathCopy({
            estudiante: {
              title: settings.wizard_path_estudiante_title || PATH_COPY_DEFAULT.estudiante.title,
              body: settings.wizard_path_estudiante_body || PATH_COPY_DEFAULT.estudiante.body,
            },
            shinobi: {
              title: settings.wizard_path_shinobi_title || PATH_COPY_DEFAULT.shinobi.title,
              body: settings.wizard_path_shinobi_body || PATH_COPY_DEFAULT.shinobi.body,
            },
          })
        }
      } catch (e) {
        setErrors(['Error cargando catálogos. Recarga la página.'])
      } finally {
        setLoading(false)
      }
    }
    initialize()
  }, [])

  useEffect(() => {
    if (characters.length > 0 && numEmptySlots <= 0) {
      navigate('/mis-personajes')
    }
  }, [characters, numEmptySlots, navigate])

  const validatePath = () => {
    const errs = []
    if (path !== 'estudiante' && path !== 'shinobi') {
      errs.push('Elige si quieres ser Estudiante de academia o Shinobi')
    }
    setErrors(errs)
    return errs.length === 0
  }

  const validateIdentity = () => {
    const errs = []
    const name = (identity.name || '').trim()
    if (!name) errs.push('El nombre es requerido')
    if (name.length < 2 || name.length > 30) errs.push('El nombre debe tener entre 2 y 30 caracteres')
    if (!NAME_REGEX.test(name)) errs.push('El nombre solo puede contener letras, espacios, apóstrofes y guiones')

    const age = parseInt(identity.age, 10)
    if (Number.isNaN(age) || age < MIN_AGE || age > MAX_AGE) {
      errs.push(`La edad debe estar entre ${MIN_AGE} y ${MAX_AGE}`)
    }

    if (!isStudent) {
      if (!identity.gender) errs.push('El género es requerido')
      if (!identity.dominantHand) errs.push('La mano dominante es requerida')
      if (!identity.bloodType) errs.push('El grupo sanguíneo es requerido')
    }

    setErrors(errs)
    return errs.length === 0
  }

  const validateOrigin = () => {
    const errs = []
    if (!origin.clan) errs.push('El clan es requerido')
    if (!origin.birthHanSlug) errs.push('El Han de nacimiento es requerido')
    if (!isStudent) {
      if (!origin.rank) errs.push('El rango es requerido')
      if (!origin.element) errs.push('El elemento es requerido')
    }
    setErrors(errs)
    return errs.length === 0
  }

  const validateStats = () => {
    if (statsValid) {
      setErrors([])
      return true
    }
    setErrors([`Debes completar la asignación de atributos (total ${TOTAL_POINTS}: 20 aleatorios + 30 manuales).`])
    return false
  }

  const validateDescriptions = () => {
    const errs = []
    if (!descriptions.physical.trim()) errs.push('La descripción física es requerida')
    if (!descriptions.psychological.trim()) errs.push('La descripción psicológica es requerida')
    setErrors(errs)
    return errs.length === 0
  }

  const validateCurrentStep = () => {
    if (stepId === 'path') return validatePath()
    if (stepId === 'identity') return validateIdentity()
    if (stepId === 'origin') return validateOrigin()
    if (stepId === 'attributes') return validateStats()
    if (stepId === 'descriptions') return validateDescriptions()
    return true
  }

  const next = () => {
    if (!validateCurrentStep()) return
    setErrors([])
    setStep((s) => Math.min(s + 1, steps.length - 1))
  }

  const prev = () => {
    setErrors([])
    setStep((s) => {
      const nextStep = Math.max(0, s - 1)
      if (nextStep === 0) setPath(null)
      return nextStep
    })
  }

  const choosePath = (nextPath) => {
    setPath(nextPath)
    setErrors([])
    setStats(emptyStats())
    setStatsValid(false)
    setAllocKey((k) => k + 1)
    setStep(1)
  }

  const onIdentityChange = (e) => {
    const { name, value } = e.target
    setIdentity((cur) => ({ ...cur, [name]: value }))
  }
  const onOriginChange = (e) => {
    const { name, value } = e.target
    setOrigin((cur) => ({ ...cur, [name]: value }))
  }

  const onSubmit = async () => {
    if (!validatePath() || !validateIdentity() || !validateOrigin() || !validateDescriptions()) return
    if (!isStudent && !validateStats()) return
    setSubmitting(true)
    setErrors([])

    try {
      const payload = {
        name: identity.name.trim(),
        gender: identity.gender || null,
        age: parseInt(identity.age, 10),
        dominantHand: isStudent ? identity.dominantHand || null : identity.dominantHand,
        bloodType: isStudent ? identity.bloodType || null : identity.bloodType,
        clan: origin.clan,
        birthHanSlug: origin.birthHanSlug,
        rank: isStudent ? 'Genin' : origin.rank,
        element: isStudent ? null : origin.element,
        descriptions,
        history,
        academyMode: isStudent && !staffFlags.sensei,
      }

      if (canCreateStaffChars) {
        payload.isStaffCharacter = Boolean(staffFlags.eventCharacter || staffFlags.sensei)
        payload.isAcademySensei = Boolean(staffFlags.sensei)
      }

      if (!isStudent) {
        payload.stats = stats
        payload.derived = { PV, CK }
      }

      const response = await HttpClient().post('/api/v1/characters/create', payload)
      navigate(`/personajes/${response.data.character.slug}`)
    } catch (error) {
      console.error('Error creating character:', error)
      // HttpClient rechaza con { message, status } (no axios error.response)
      const apiMessage = error?.message || error?.response?.data?.message
      if (apiMessage) setErrors([apiMessage])
      else setErrors(['Error creando el personaje. Inténtalo de nuevo.'])
    } finally {
      setSubmitting(false)
    }
  }

  function renderStepHeader(title, subtitle) {
    return (
      <div className="character-creation__info">
        <h3>{title}</h3>
        {subtitle ? <p>{subtitle}</p> : null}
      </div>
    )
  }

  function renderPathStep() {
    return (
      <>
        {renderStepHeader(
          '¿Cómo quieres empezar?',
          'Elige el camino de tu personaje. Podrás seguir con la ficha en los siguientes pasos.'
        )}
        <div className="character-creation__form">
          {errors.length > 0 && <FormErrors errors={errors} />}

          <div className="wizard-path-grid" role="group" aria-label="Camino del personaje">
            {(['estudiante', 'shinobi']).map((key) => {
              const copy = pathCopy[key]
              const selected = path === key
              return (
                <button
                  key={key}
                  type="button"
                  className={`wizard-path-card${selected ? ' wizard-path-card--selected' : ''}`}
                  onClick={() => choosePath(key)}
                  aria-pressed={selected}
                >
                  <span className="wizard-path-card__title">{copy.title}</span>
                  <span className="wizard-path-card__body">{copy.body}</span>
                </button>
              )
            })}
          </div>
        </div>
      </>
    )
  }

  function renderIdentityStep() {
    return (
      <>
        {renderStepHeader(
          'Identidad',
          isStudent
            ? 'Nombre y edad. El género es opcional en el camino de Estudiante.'
            : 'Nombre, género, edad, mano dominante y grupo sanguíneo.'
        )}
        <div className="character-creation__form">
          {errors.length > 0 && <FormErrors errors={errors} />}

          <div className="form-row">
            <Field
              theme="parchment"
              label="Nombre del Personaje"
              name="name"
              value={identity.name}
              onChange={onIdentityChange}
            />
          </div>

          <div className="form-row">
            <Select
              theme="parchment"
              label={isStudent ? 'Género (opcional)' : 'Género'}
              name="gender"
              value={identity.gender}
              onChange={onIdentityChange}
              options={GENDERS}
              placeholder="Selecciona género"
            />
          </div>

          <div className="form-row">
            <div className="field">
              <label className="wizard-label" htmlFor="age">
                Edad
              </label>
              <input
                id="age"
                name="age"
                type="number"
                min={MIN_AGE}
                max={MAX_AGE}
                value={identity.age}
                onChange={onIdentityChange}
                className="wizard-input"
                required
              />
            </div>
          </div>

          {!isStudent && (
            <>
              <div className="form-row">
                <Select
                  theme="parchment"
                  label="Mano dominante"
                  name="dominantHand"
                  value={identity.dominantHand}
                  onChange={onIdentityChange}
                  options={HANDS}
                  placeholder="Selecciona mano dominante"
                />
              </div>

              <div className="form-row">
                <Select
                  theme="parchment"
                  label="Grupo sanguíneo"
                  name="bloodType"
                  value={identity.bloodType}
                  onChange={onIdentityChange}
                  options={BLOOD_TYPES}
                  placeholder="Selecciona grupo sanguíneo"
                />
              </div>
            </>
          )}
        </div>
      </>
    )
  }

  function renderOriginStep() {
    return (
      <>
        {renderStepHeader(
          'Origen',
          isStudent
            ? 'Elige tu clan y Han de nacimiento.'
            : 'Elige tu clan, Han de nacimiento, rango inicial y elemento.'
        )}
        <div className="character-creation__form">
          {errors.length > 0 && <FormErrors errors={errors} />}

          <div className="form-row">
            <Select
              theme="parchment"
              label="Clan"
              name="clan"
              value={origin.clan}
              onChange={onOriginChange}
              options={clanOptions}
              placeholder="Selecciona un clan"
            />
          </div>

          <div className="form-row">
            <Select
              theme="parchment"
              label="Han de nacimiento"
              name="birthHanSlug"
              value={origin.birthHanSlug}
              onChange={onOriginChange}
              options={hanOptions}
              placeholder="Selecciona un Han"
            />
          </div>

          {!isStudent && (
            <>
              <div className="form-row">
                <Select
                  theme="parchment"
                  label="Rango"
                  name="rank"
                  value={origin.rank}
                  onChange={onOriginChange}
                  options={RANKS}
                  placeholder="Selecciona rango"
                />
              </div>

              <div className="form-row">
                <Select
                  theme="parchment"
                  label="Elemento"
                  name="element"
                  value={origin.element}
                  onChange={onOriginChange}
                  options={ELEMENTS}
                  placeholder="Selecciona elemento"
                />
              </div>
            </>
          )}
        </div>
      </>
    )
  }

  function renderAttributesStep() {
    return (
      <>
        {renderStepHeader(
          'Atributos',
          `20 puntos aleatorios (1–4 por atributo) + 30 a repartir (total ${TOTAL_POINTS}). Puedes volver a tirar la base una vez.`
        )}
        <div className="character-creation__form">
          {errors.length > 0 && <FormErrors errors={errors} />}
          <StatAllocationPanel
            key={allocKey}
            level={createLevel}
            title="Asignación"
            onValidChange={onAllocChange}
          />
        </div>
      </>
    )
  }

  function renderDescriptionsStep() {
    return (
      <>
        {renderStepHeader('Descripciones', 'Describe la apariencia física y la psicología de tu personaje.')}
        <div className="character-creation__form">
          {errors.length > 0 && <FormErrors errors={errors} />}

          <div className="form-row">
            <label className="wizard-label" htmlFor="physical">
              Descripción física
            </label>
            <textarea
              id="physical"
              value={descriptions.physical}
              onChange={(e) => setDescriptions((d) => ({ ...d, physical: e.target.value }))}
              rows={5}
              className="wizard-textarea"
              placeholder="Altura, complexión, rasgos distintivos, vestimenta..."
            />
          </div>

          <div className="form-row">
            <label className="wizard-label" htmlFor="psychological">
              Descripción psicológica
            </label>
            <textarea
              id="psychological"
              value={descriptions.psychological}
              onChange={(e) => setDescriptions((d) => ({ ...d, psychological: e.target.value }))}
              rows={5}
              className="wizard-textarea"
              placeholder="Carácter, motivaciones, virtudes y defectos..."
            />
          </div>
        </div>
      </>
    )
  }

  function renderHistoryStep() {
    return (
      <>
        {renderStepHeader('Historia', 'Escribe la historia del personaje. Soporta BBCode con vista previa.')}
        <div className="character-creation__form">
          <BBCodeEditor value={history} onChange={setHistory} height="260px" enableRPCode={false} />
        </div>
      </>
    )
  }

  function renderSummaryStep() {
    const pathLabel = isStudent ? pathCopy.estudiante.title : pathCopy.shinobi.title
    return (
      <>
        {renderStepHeader('Resumen', 'Revisa tu ficha antes de crear el personaje.')}
        <div className="character-creation__form">
          {errors.length > 0 && <FormErrors errors={errors} />}

          <div className="form-row">
            <div className="wizard-summary-list">
              Camino: <strong>{pathLabel}</strong>
              {' · '}
              Nivel inicial: <strong>{createLevel}</strong>
            </div>
          </div>

          <div className="form-row">
            <div className="wizard-summary-grid">
              <div>
                <h4 className="wizard-section__title">Identidad</h4>
                <div className="wizard-summary-list">
                  <div>
                    Nombre: <strong>{identity.name}</strong>
                  </div>
                  <div>
                    Género: <strong>{identity.gender || '—'}</strong>
                  </div>
                  <div>
                    Edad: <strong>{identity.age}</strong>
                  </div>
                  {!isStudent && (
                    <>
                      <div>
                        Mano: <strong>{identity.dominantHand}</strong>
                      </div>
                      <div>
                        Sangre: <strong>{identity.bloodType}</strong>
                      </div>
                    </>
                  )}
                </div>
              </div>
              <div>
                <h4 className="wizard-section__title">Origen</h4>
                <div className="wizard-summary-list">
                  <div>
                    Clan: <strong>{origin.clan}</strong>
                  </div>
                  <div>
                    Han de nacimiento: <strong>{selectedHanName}</strong>
                  </div>
                  {!isStudent && (
                    <>
                      <div>
                        Rango: <strong>{origin.rank}</strong>
                      </div>
                      <div>
                        Elemento: <strong>{origin.element}</strong>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {!isStudent && (
            <div className="form-row">
              <h4 className="wizard-section__title">Atributos</h4>
              <div className="wizard-attrs-grid">
                {STAT_KEYS.map(({ key, label }) => (
                  <div key={key}>
                    {label}: <strong>{stats[key]}</strong>
                  </div>
                ))}
              </div>
              <div className="wizard-summary-list" style={{ marginTop: 12 }}>
                PV: <strong>{PV}</strong> | CK: <strong>{CK}</strong>
              </div>
            </div>
          )}

          <div className="form-row">
            <h4 className="wizard-section__title">Descripciones</h4>
            <div className="wizard-summary-list">
              Física:{' '}
              {descriptions.physical
                ? descriptions.physical.slice(0, 160) + (descriptions.physical.length > 160 ? '…' : '')
                : '-'}
            </div>
            <div className="wizard-summary-list">
              Psicológica:{' '}
              {descriptions.psychological
                ? descriptions.psychological.slice(0, 160) +
                  (descriptions.psychological.length > 160 ? '…' : '')
                : '-'}
            </div>
          </div>

          {canCreateStaffChars ? (
            <div className="form-row wizard-staff-flags">
              <h4 className="wizard-section__title">Personaje de staff</h4>
              <p className="wizard-staff-flags__hint">
                No aparece en «Mis shinobis» ni consume slots. Cualquier staff puede usarlo.
              </p>
              <label className="wizard-staff-flags__option">
                <input
                  type="checkbox"
                  checked={staffFlags.eventCharacter || staffFlags.sensei}
                  onChange={(e) => {
                    const checked = e.target.checked
                    setStaffFlags((cur) => ({
                      eventCharacter: checked,
                      sensei: checked ? cur.sensei : false,
                    }))
                  }}
                />
                <span>
                  <strong>Personaje de evento</strong>
                  <span className="wizard-staff-flags__detail">
                    {' '}
                    — NPC / PJ compartido del pool staff
                  </span>
                </span>
              </label>
              <label className="wizard-staff-flags__option">
                <input
                  type="checkbox"
                  checked={staffFlags.sensei}
                  onChange={(e) => {
                    const checked = e.target.checked
                    setStaffFlags((cur) => ({
                      eventCharacter: checked || cur.eventCharacter,
                      sensei: checked,
                    }))
                  }}
                />
                <span>
                  <strong>Sensei</strong>
                  <span className="wizard-staff-flags__detail">
                    {' '}
                    — Sensei de academia (asignable en exámenes)
                  </span>
                </span>
              </label>
            </div>
          ) : null}
        </div>
      </>
    )
  }

  const renderStep = () => {
    switch (stepId) {
      case 'path':
        return renderPathStep()
      case 'identity':
        return renderIdentityStep()
      case 'origin':
        return renderOriginStep()
      case 'attributes':
        return renderAttributesStep()
      case 'descriptions':
        return renderDescriptionsStep()
      case 'history':
        return renderHistoryStep()
      case 'summary':
        return renderSummaryStep()
      default:
        return null
    }
  }

  if (loading) {
    return (
      <div className="page">
        <div className="character-creation">
          <div className="character-creation__header">
            <span className="character-creation__eyebrow">忍 · Creación</span>
            <h1 className="character-creation__title">Creación de Personaje</h1>
          </div>
          <div className="character-creation__loading">
            <div className="loading-spinner"></div>
            <p>Cargando datos...</p>
          </div>
        </div>
      </div>
    )
  }

  const lastStepIndex = steps.length - 1
  const showNext = stepId !== 'path' && step < lastStepIndex
  const showCreate = stepId === 'summary'

  return (
    <div className="page">
      <div className="character-creation">
        <div className="character-creation__header">
          <span className="character-creation__eyebrow">忍 · Creación</span>
          <h1 className="character-creation__title">Creación de Personaje</h1>
          <div className="character-creation__slots">
            <span className="slots-info">
              Slots disponibles: <strong>{numEmptySlots}</strong> de <strong>{maxSlots}</strong>
            </span>
          </div>
          <div className="character-creation__step">
            Paso <strong>{step + 1}</strong> de <strong>{steps.length}</strong>
            {path ? (
              <>
                {' '}
                · <strong>{isStudent ? 'Estudiante' : 'Shinobi'}</strong>
              </>
            ) : null}
          </div>
        </div>

        <div className="character-creation__content">
          {renderStep()}

          <div className="character-creation__actions">
            <Button type="button" onClick={() => navigate('/mis-personajes')} disabled={submitting}>
              Cancelar
            </Button>

            {step > 0 && (
              <Button type="button" onClick={prev} disabled={submitting}>
                Anterior
              </Button>
            )}

            {showNext && (
              <Button type="button" onClick={next} disabled={submitting}>
                Siguiente
              </Button>
            )}

            {showCreate && (
              <Button type="button" onClick={onSubmit} disabled={submitting}>
                {submitting ? 'Creando...' : 'Crear Personaje'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
